import type { Claim, Debit } from 'interfaces';
import {
  CLAIM_TARIFF_STATUS,
  claimTariffStatusEnumValues,
} from '@billing/validation/tariff';
import type { ClaimTariffStatusValue } from '@billing/validation/tariff';
import { rollUpClaimTariffStatuses } from '@billing/validation';
import { getProviderStatusDisplay } from 'utils/providerClaimStatus';

export type ClaimRowType = 'claim' | 'tariff';

type ClaimTariff = Claim['tariffs'][number];

interface TariffStatusSource {
  id: string;
  status?: string | null;
}

interface TariffAmountSource {
  id: string;
  rate?: string | number | null;
  total?: string | number | null;
}

export interface ClaimParentRow extends Claim {
  rowType: 'claim';
  rowId: string;
  parentClaimId: string;
  parentClaim: Claim;
  subRows: ClaimTariffRow[];
  tariffId?: never;
}

export interface ClaimTariffRow extends Claim {
  rowType: 'tariff';
  rowId: string;
  parentClaimId: string;
  parentClaim: Claim;
  tariffId: string;
  tariffs: [ClaimTariff];
  debits: Debit[];
  subRows?: never;
}

export type ClaimTableRow = ClaimParentRow | ClaimTariffRow;

export interface ClaimTariffSelectionItem {
  claimID: string;
  tariffID: string;
}

interface ClaimDebitSource {
  debits?: Debit[];
  payorID?: string | null;
}

const claimTariffStatusSet = new Set<string>(claimTariffStatusEnumValues);

export const buildClaimRowId = (claimId: string) => `claim:${claimId}`;

export const buildTariffRowId = (claimId: string, tariffId: string) =>
  `tariff:${claimId}:${tariffId}`;

export const parseRowId = (rowId: string) => {
  if (rowId.startsWith('claim:')) {
    return {
      kind: 'claim' as const,
      claimID: rowId.replace('claim:', ''),
      tariffID: undefined,
    };
  }

  if (rowId.startsWith('tariff:')) {
    const [, claimID, tariffID] = rowId.split(':');
    return {
      kind: 'tariff' as const,
      claimID,
      tariffID,
    };
  }

  return {
    kind: 'claim' as const,
    claimID: rowId,
    tariffID: undefined,
  };
};

export const isClaimTableRow = (row: unknown): row is ClaimTableRow =>
  typeof row === 'object' &&
  row !== null &&
  'rowType' in row &&
  (row.rowType === 'claim' || row.rowType === 'tariff');

export const isClaimParentRow = (row: unknown): row is ClaimParentRow =>
  isClaimTableRow(row) && row.rowType === 'claim';

export const isClaimTariffRow = (row: unknown): row is ClaimTariffRow =>
  isClaimTableRow(row) && row.rowType === 'tariff';

export const getParentClaim = (row: ClaimTableRow | Claim): Claim => {
  if (!isClaimTableRow(row) || isClaimParentRow(row)) return row;
  return row.parentClaim;
};

const getDebitCreatedAtMillis = (debit: Debit) => {
  const millis = debit.createdAt ? new Date(debit.createdAt).getTime() : 0;
  return Number.isNaN(millis) ? 0 : millis;
};

const compareDebitsNewestFirst = (left: Debit, right: Debit) => {
  const createdAtDifference =
    getDebitCreatedAtMillis(right) - getDebitCreatedAtMillis(left);
  if (createdAtDifference !== 0) return createdAtDifference;

  return String(right.id).localeCompare(String(left.id));
};

export const getClaimRowIds = (claimId: string): string[] => [claimId];

export const buildClaimRowSelection = (
  claimRowId: string,
  selected: boolean,
  tariffRowIds: string[] = []
): Record<string, boolean> => ({
  [claimRowId]: selected,
  ...Object.fromEntries(tariffRowIds.map((rowId) => [rowId, selected])),
});

export const getDisplayedTariff = (claim: Claim): ClaimTariff | null =>
  claim.tariffs?.[0] || null;

export const getTariffDebits = (
  claim: ClaimDebitSource,
  tariffId: string | null | undefined
): Debit[] => {
  if (!tariffId) return claim.debits ?? [];

  return (claim.debits ?? []).filter(
    (debit) =>
      debit.tariffID === tariffId &&
      (!claim.payorID || !debit.payorID || debit.payorID === claim.payorID)
  );
};

export const getLatestDisplayedDebit = (
  claim: ClaimDebitSource,
  tariffId?: string | null
): Debit | null =>
  getTariffDebits(claim, tariffId).sort(compareDebitsNewestFirst)[0] ?? null;

export const getLatestNonRetractionDisplayedDebit = (
  claim: ClaimDebitSource,
  tariffId?: string | null
): Debit | null =>
  getTariffDebits(claim, tariffId)
    .filter((debit) => debit.type !== 'RETRACTION')
    .sort(compareDebitsNewestFirst)[0] ?? null;

export const getLatestClaimPayorDebit = (
  claim: ClaimDebitSource & { id: string },
  tariffID: string
): Debit | null =>
  (claim.debits ?? [])
    .filter(
      (debit) =>
        debit.claimID === claim.id &&
        debit.tariffID === tariffID &&
        debit.payorID === claim.payorID
    )
    .sort(compareDebitsNewestFirst)[0] ?? null;

export const getLatestNestedPayorDebit = (
  claim: ClaimDebitSource,
  tariffID: string
): Debit | null =>
  (claim.debits ?? [])
    .filter(
      (debit) => debit.tariffID === tariffID && debit.payorID === claim.payorID
    )
    .sort(compareDebitsNewestFirst)[0] ?? null;

export const getDisplayedDebits = (
  claim: ClaimDebitSource,
  tariffId?: string | null
): Debit[] => getTariffDebits(claim, tariffId);

export const getClaimTariffRowDebits = (row: ClaimTariffRow): Debit[] => {
  if (row.debits?.length) return row.debits;

  return (row.parentClaim?.debits ?? []).filter(
    (debit) => debit.tariffID === row.tariffId
  );
};

const getPersistedTariffStatusesForClaim = (
  claim: Claim
): ClaimTariffStatusValue[] => {
  const tariffs = Array.isArray(claim.tariffs) ? claim.tariffs : [];
  return tariffs
    .map((tariff) => tariff.status?.trim())
    .filter(
      (status): status is ClaimTariffStatusValue =>
        Boolean(status) && claimTariffStatusSet.has(status)
    );
};

export const getClaimDisplayStatus = (
  claim: Claim,
  options?: { isProvider?: boolean }
): string => {
  const statuses = getPersistedTariffStatusesForClaim(claim);
  return rollUpClaimTariffStatuses(
    statuses,
    options?.isProvider ? getProviderStatusDisplay : undefined
  );
};

export const getTariffStatus = (
  claim: Claim,
  tariff: TariffStatusSource
): string => {
  const persistedTariffStatus = tariff.status?.trim();
  if (persistedTariffStatus) return persistedTariffStatus;

  // TODO(claim-status-migration-cleanup): Remove debit/claim fallbacks after
  // all historical tariffs are backfilled with non-null claim_tariff.status.
  const latestDebit = getLatestDisplayedDebit(claim, tariff.id);
  const debitStatus = latestDebit?.status?.trim();

  if (debitStatus) return debitStatus;

  const requestedAmount = Number(latestDebit?.requestedAmount ?? 0);
  const paidAmount = Number(latestDebit?.paidAmount ?? 0);

  if (paidAmount > requestedAmount && requestedAmount > 0) {
    return CLAIM_TARIFF_STATUS.OVERPAID;
  }
  if (paidAmount === requestedAmount && requestedAmount > 0) {
    return CLAIM_TARIFF_STATUS.PAID;
  }
  if (paidAmount > 0 && paidAmount < requestedAmount) {
    return CLAIM_TARIFF_STATUS.UNDERPAID;
  }

  const claimStatus = claim.status?.trim();
  if (claimStatus) return claimStatus;

  return CLAIM_TARIFF_STATUS.PENDING;
};

export const getRowDisplayStatus = (
  row: Claim | ClaimTableRow,
  options?: { isProvider?: boolean }
): string => {
  if (isClaimTariffRow(row)) {
    const [tariff] = row.tariffs;
    if (!tariff) return getClaimDisplayStatus(row, options);
    const tariffStatus = getTariffStatus(row, tariff);
    if (options?.isProvider) {
      return getProviderStatusDisplay(tariffStatus);
    }
    return tariffStatus;
  }

  return getClaimDisplayStatus(row, options);
};

export const getTariffAmounts = (claim: Claim, tariff: TariffAmountSource) => {
  const latestDebit = getLatestDisplayedDebit(claim, tariff.id);
  const requested = Number(
    latestDebit?.requestedAmount ?? tariff.total ?? tariff.rate ?? 0
  );
  const paid = Number(latestDebit?.paidAmount ?? 0);
  const outstanding = Math.max(requested - paid, 0);

  return {
    requested,
    paid,
    outstanding,
  };
};

export const getClaimPaymentRequestedAmount = (
  claim: Claim,
  tariff: TariffAmountSource
): number =>
  Number(
    getLatestClaimPayorDebit(claim, tariff.id)?.requestedAmount ??
      tariff.total ??
      tariff.rate ??
      0
  );

export const buildClaimTableRows = (claims: Claim[]): ClaimTableRow[] => {
  const rows = claims.map((claim) => {
    const claimRowId = buildClaimRowId(claim.id);
    const subRows: ClaimTariffRow[] = (claim.tariffs ?? []).map((tariff) => {
      const rowDebits = getTariffDebits(claim, tariff.id);

      return {
        ...claim,
        id: claim.id,
        tariffs: [tariff],
        debits: rowDebits,
        rowType: 'tariff',
        rowId: buildTariffRowId(claim.id, tariff.id),
        parentClaimId: claim.id,
        parentClaim: claim,
        tariffId: tariff.id,
      };
    });

    const parentRow: ClaimParentRow = {
      ...claim,
      rowType: 'claim',
      rowId: claimRowId,
      parentClaimId: claim.id,
      parentClaim: claim,
      subRows,
    };

    return parentRow;
  });

  return rows;
};

export const getTariffSelectionFromRowSelection = (
  rowSelection: Record<string, boolean>
): ClaimTariffSelectionItem[] =>
  Object.entries(rowSelection)
    .filter(([, selected]) => Boolean(selected))
    .map(([rowId]) => parseRowId(rowId))
    .filter(
      (
        parsed
      ): parsed is {
        kind: 'tariff';
        claimID: string;
        tariffID: string;
      } =>
        parsed.kind === 'tariff' && Boolean(parsed.claimID && parsed.tariffID)
    )
    .map(({ claimID, tariffID }) => ({
      claimID,
      tariffID,
    }));

export const getClaimIdsFromRowSelection = (
  rowSelection: Record<string, boolean>
): string[] =>
  Array.from(
    new Set(
      Object.entries(rowSelection)
        .filter(([, selected]) => Boolean(selected))
        .map(([rowId]) => parseRowId(rowId))
        .filter((parsed) => parsed.kind === 'claim')
        .map((parsed) => parsed.claimID)
    )
  );
